import React, { ReactNode } from 'react';
import styled from 'astroturf/react';
import { Button } from 'js/design_system/Button';

export type BannerAction = {
  type?: 'primary' | 'inverse';
  content: ReactNode;
  onClick?: () => void;
};

export type BannerProps = React.HTMLAttributes<HTMLDivElement> & {
  type?: 'info' | 'success' | 'warning' | 'danger';
  border?: 'all' | 'bottom';
  icon?: ReactNode;
  actions?: BannerAction[];
};

const IconContainer = styled.div`
  line-height: 0;
`;

const MessageContainer = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: flex-start;
  gap: 8px;

  @include for-mobile-lg-up {
    align-items: center;
  }
`;

const Container = styled.div<Pick<BannerProps, 'type' | 'border'>>`
  @import 'stylesheets/design_system/font';
  @include body__medium;

  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px;
  border-style: solid;
  border-width: 0;
  border-color: #dddde2;

  @include for-mobile-lg-up {
    flex-wrap: nowrap;
  }

  &.type-info {
    background-color: #f4f7fc;
    border-color: #0645b1;

    ${IconContainer} {
      color: #0645b1;
    }
  }

  &.type-success {
    background-color: #f0f8f1;
    border-color: #048c16;

    ${IconContainer} {
      color: #048c16;
    }
  }

  &.type-warning {
    background-color: #f9f6ed;
    border-color: #b08920;

    ${IconContainer} {
      color: #b08920;
    }
  }

  &.type-danger {
    background-color: #fbf0f0;
    border-color: #b60000;

    ${IconContainer} {
      color: #b60000;
    }
  }

  &.border-all {
    border-radius: 7px;
    border-width: 1px;
  }

  &.border-bottom {
    border-bottom-width: 1px;
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex-basis: 100%;

  @include for-mobile-lg-up {
    flex-basis: auto;
  }
`;

const ActionButton = styled(Button)`
  height: 44px;

  @include for-mobile-lg-up {
    height: 32px;
    padding: 0 12px;
  }
`;

/**
 * A banner component. This component is commonly used to display
 * contextual messages to users. It draws more attention than simple text,
 * so it should be used with care.
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Banner } from 'js/design_system/Banner';
 *
 * <Banner
 *   type='info'
 *   actions={[
 *     { type: 'inverse', content: 'Cancel', onClick: () => alert('Cancel!') },
 *     { content: 'Confirm', onClick: () => alert('Confirm!') },
 *   ]}
 * >
 *   I'm an important banner! Click an action to get started.
 * </Banner>
 * ```
 */
export const Banner: React.FC<BannerProps> = ({
  type,
  border = 'all',
  icon,
  actions = [],
  children,
  ...props
}) => {
  return (
    <Container type={type} border={border} {...props}>
      <MessageContainer>
        {icon && <IconContainer>{icon}</IconContainer>}
        {children}
      </MessageContainer>

      {actions.length > 0 && (
        <ActionsContainer>
          {actions.map(
            ({ type = 'primary', content, onClick = () => null }, index) => {
              return (
                <ActionButton
                  key={index}
                  inverse={type === 'inverse'}
                  onClick={onClick}
                >
                  {content}
                </ActionButton>
              );
            },
          )}
        </ActionsContainer>
      )}
    </Container>
  );
};
